import React, { useEffect, useState } from 'react';
import WebLinkTable from '../../../components/WebLinkTable/WebLinkTable';
import { updateFlashBar, updateIsLoading, updateReviewTab } from '../../../redux/action';
import instance from '../../../service/axiosInstane';
import { useDispatch, useSelector } from 'react-redux';
import { flashBarTheme } from '../../../config/flashBar';
import qs from 'qs';
import { SortOrderType, WeblinkSource } from '../../../config/enum';
import { StateProps } from '../../../redux/reducer';
import { handlingMoreCharactor, totalPages } from '../../../config/helpers';
import Button from '../../../components/Button/Button';
import { successMessage } from '../../../config/keyword';
import EllipsisHanlder from '../../../components/Ellipsis/EllipsisHanlder';
import { markedDmcaCountLables } from '../../../config/constants';

type ReviewStateProps = {
  categoryId: string;
  categoryIdName: string;
  categoryLabelId: string;
  categoryLabelName: string;
  id: string;
  ignoreCount: number;
  isDmcaGenerated: boolean;
  isReviewed: boolean;
  keywordId: string;
  keywordText: string;
  markedDmcaCount: number;
  organizationId: string;
  revisitCount: number;
  siteHost: string;
  siteId: string;
  siteOrganizationId: string;
  siteOrganizationIndexId: string;
  siteUrl: string;
};

type SearchResultRequestProps = {
  Page: number;
  PageSize: number;
  SearchText: string;
  SortBy: string;
  SortOrder: string;
};

type ResponseProps = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  results?: ResultProps[];
};

type ResultProps = {
  matchContent: string;
  isDmcaGenerated: boolean;
  siteCrawlId: string;
  weblinkId: string;
  weblinkPreference: number;
  weblinkUrl: string;
};

type PreferenceProps = {
  isDmcaGenerated: boolean;
  matchContent: string;
  siteCrawlId: string;
  weblinkId: string;
  weblinkPreference: number;
  weblinkUrl: string;
};

const WebsiteSearchResults = () => {
  const dispatch = useDispatch();
  const reviewTab = useSelector((state: StateProps) => state.reviewTab);
  const [showSearchResult, setShowSearchResult] = useState(true);
  const [reviewState, setReviewState] = useState<ReviewStateProps>({
    categoryId: '',
    categoryIdName: '',
    categoryLabelId: '',
    categoryLabelName: '',
    id: '',
    ignoreCount: 0,
    isDmcaGenerated: false,
    isReviewed: false,
    keywordId: '',
    keywordText: '',
    markedDmcaCount: 0,
    organizationId: '',
    revisitCount: 0,
    siteHost: '',
    siteId: '',
    siteOrganizationId: '',
    siteOrganizationIndexId: '',
    siteUrl: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [searchResultRequest, setSearchResultRequest] =
    useState<SearchResultRequestProps>({
      Page: page,
      PageSize: limit,
      SearchText: '',
      SortBy: '',
      SortOrder: SortOrderType.ASC,
    });
  const [searchState, setSearchState] = useState<ResultProps[]>([]);
  
  useEffect(() => {
    getSiteOrgReview();
    getReviewSearchResult(searchResultRequest);
  }, []);

  const getSiteOrgReview = async () => {
    dispatch(updateIsLoading(true));
    await instance
      .get(
        `site-review/site-organization-index/${reviewTab.siteOrgIndexID}/review/site-keyword-hit/${reviewTab.keywordID}`
      )
      .then((response) => {
        if (response.status === 200) {
          setReviewState(response.data);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const getReviewSearchResult = async (obj: SearchResultRequestProps) => {
    dispatch(updateIsLoading(true));
    const queryString = qs.stringify(obj, { arrayFormat: 'repeat' });
    const url = `/site-review/site-organization-index/${reviewTab.siteOrgIndexID}/review/site-keyword-hit/${reviewTab.keywordID}/weblinks?${queryString}`;
    await instance
      .get(url)
      .then((response) => {
        if (response.status === 200) {
          gotResponse(response.data);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const gotResponse = (data: ResponseProps) => {
    if (data.results !== null) {
      setSearchState(data?.results as ResultProps[]);
      setTotalRowCount(data.totalCount);
      dispatch(updateIsLoading(false));
    } else {
      setSearchState([]);
      dispatch(updateIsLoading(false));
    }
  };

  const paginateHandler = {
    onClickPageNumber: (page: number) => {
      getReviewSearchResult({ ...searchResultRequest, Page: page, PageSize: limit });
      setPage(page);
    },
    onNextClick: () => {
      if (page < totalPages(totalRowCount, limit)) {
        getReviewSearchResult({
          ...searchResultRequest,
          Page: page + 1,
          PageSize: limit,
        });
        setPage(page + 1);
      }
    },
    onPrevClick: () => {
      if (page > 1) {
        getReviewSearchResult({
          ...searchResultRequest,
          Page: page - 1,
          PageSize: limit,
        });
        setPage(page - 1);
      }
    },
    onFirstClick: () => {
      if (page !== 1) {
        getReviewSearchResult({
          ...searchResultRequest,
          Page: 1,
          PageSize: limit,
        });
        setPage(1);
      }
    },
    onLastClick: () => {
      if (page !== totalPages(totalRowCount, limit)) {
        getReviewSearchResult({
          ...searchResultRequest,
          Page: totalPages(totalRowCount, limit),
          PageSize: limit,
        });
        setPage(totalPages(totalRowCount, limit));
      }
    },

    limitHandler: (item: number) => {
      getReviewSearchResult({
        ...searchResultRequest,
        Page: 1,
        PageSize: item,
      });
      setLimit(item);
      setPage(1);
    },
  };

  const markYourPreference = (item: PreferenceProps, status: number) => {
    const obj = {
      weblinkUrl: item.weblinkUrl,
      keywordId: reviewState.keywordId,
      weblinkPreference: status,
      siteOrganizationId: reviewState.siteOrganizationId,
      weblinkSource: WeblinkSource.Index,
    };
    const updateObj = {
      weblinkPreference: status,
    };
    if (!item.isDmcaGenerated) {
      if (item.weblinkId === null) {
        addPreference(obj);
      } else {
        updatePreference(updateObj, item);
      }
    }
  };

  const updatePreference = (obj: any, row: any) => {
    dispatch(updateIsLoading(true));
    instance
      .put(`/weblinks/${row.weblinkId}/marke-preference`, obj)
      .then((response) => {
        if (response.status === 200) {
          const obj = {
            isOpen: true,
            message: successMessage.MARKED_PREFERENCE,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
          getReviewSearchResult({
            ...searchResultRequest,
            Page: page,
            PageSize: limit,
          });
          getSiteOrgReview();
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const addPreference = (obj: any) => {
    dispatch(updateIsLoading(true));
    instance
      .post(`/weblinks`, obj)
      .then((response) => {
        if (response.status === 200) {
          getReviewSearchResult({
            ...searchResultRequest,
            Page: page,
            PageSize: limit,
          });
          getSiteOrgReview();
          const obj = {
            isOpen: true,
            message: successMessage.MARKED_PREFERENCE,
            type: flashBarTheme.SUCCESS,
          };
          dispatch(updateFlashBar(obj));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const reviewDone = () => {
    dispatch(updateIsLoading(true));
    instance
      .put(
        `site-review/site-organization-index/${reviewTab.siteOrgIndexID}/review/site-keyword-hit/${reviewTab.keywordID}`
      )
      .then((response) => {
        if (response.status === 200) {
          const obj = {
            isOpen: true,
            message: successMessage.REVIEW_SUCCESS,
            type: flashBarTheme.SUCCESS,
          };
            dispatch(
                    updateReviewTab({
                      ...reviewTab,
                      tabName: 'keyword',
                      siteOrgIndexID: reviewTab.siteOrgIndexID,
                      categoryID: reviewTab.categoryID,
                      keywordID: reviewTab.keywordID,
                    })
                  );
          getSiteOrgReview();
          getReviewSearchResult({
            ...searchResultRequest,
            Page: page,
            PageSize: limit,
          });
          dispatch(updateFlashBar(obj));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  return (
    <div className="mt-3">
      <div className="px-4">
        <div className="p-2 rounded-sm shadow-sm flex items-center justify-between">
          <div>Search Result</div>
          <div
            className="cursor-pointer"
            onClick={() => setShowSearchResult(!showSearchResult)}
          >
            <i
              className={`fa-solid fa-caret-${showSearchResult ? 'up' : 'down'}`}
            ></i>
          </div>
        </div>

        <div className="shadow-md p-2 text-sm w-full">
          {showSearchResult && (
            <div className="w-full mb-4 p-4 ">
              <div className="w-full sm:flex items-center justify-between">
                <div className="cursor-pointer mb-3">
                  <div className={`font-semibold text-[#1b2e49]`}>Website</div>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${reviewState.siteHost}`}
                    className="text-blue-500 cursor-pointer"
                  >
                    {reviewState.siteHost}
                  </a>
                </div>
                <div className="mb-2">
                  <div className="text-black text-sm">
                    <EllipsisHanlder
                      word={reviewState.categoryIdName}
                      NoOfChar={20}
                      label={'Category'}
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <div className="text-black text-sm">
                    <EllipsisHanlder
                      word={reviewState.categoryLabelName}
                      NoOfChar={20}
                      label={'Category label'}
                    />
                  </div>
                </div>
              </div>
              <div className="font-semibold mt-4 text-[#1b2e49]">Keyword Text:</div>
              <div className="text-black text-sm">{reviewState.keywordText}</div>
            </div>
          )}

          <div className="flex lg:items-center justify-between ">
            <div className="lg:flex items-center lg:space-x-4">
              <div className="w-[200px] border border-red-400 text-center sm:flex items-center justify-between rounded-md shadow-sm  mt-2">
                <div className="text-[16px]  text-[#1b2e49] px-4">
                  {reviewState.markedDmcaCount}
                </div>
                <div className="text-sm bg-red-400 w-full text-white p-1 text-center rounded-r-sm">
                  {markedDmcaCountLables.markedDMCA}
                </div>
              </div>

              <div className="w-[200px] border border-yellow-400 text-center sm:flex items-center justify-between  rounded-md shadow-sm  mt-2">
                <div className="text-[16px] text-[#1b2e49] px-4">
                  {reviewState.revisitCount}
                </div>
                <div className="text-sm bg-yellow-400 w-full text-white p-1 text-center rounded-r-sm">
                  {markedDmcaCountLables.markedRevisit}
                </div>
              </div>

              <div className="w-[200px] border border-green-400 text-center sm:flex items-center justify-between rounded-md shadow-sm  mt-2">
                <div className="text-[16px]  text-[#1b2e49] px-4">
                  {reviewState.ignoreCount}
                </div>
                <div className="text-sm bg-green-400 w-full text-white p-1 text-center rounded-r-sm">
                  {markedDmcaCountLables.ignored}
                </div>
              </div>
            </div>

            {reviewState.isReviewed === false && searchState.length > 0 && (
              <div>
                <Button
                  label={'Review Done'}
                  type={'button'}
                  buttonType={'primary'}
                  onClick={reviewDone}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-2">
          <div className="mt-5">
            <WebLinkTable
              state={searchState}
              reviewstate={reviewState}
              markYourPreference={markYourPreference}
              page={page}
              limit={limit}
              paginateHandler={paginateHandler}
              totalRowCount={totalRowCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteSearchResults;
